import { create } from 'zustand';
import { isNative } from '../native/platform';
import { sessionHooks, useSession } from '../store/session';
import { api } from '../sync/api';

const FLAG = 'hmawiw-push';

export type PushStatus = 'unsupported' | 'off' | 'on' | 'denied';

export interface PushState {
  status: PushStatus;
  busy: boolean;
  error: string | null;
  enable(): Promise<void>;
  disable(): Promise<void>;
  /** Asks the server for a test notification; false when the request itself failed. */
  test(): Promise<boolean>;
}

export function pushSupported(): boolean {
  return !isNative && typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function wanted(): boolean {
  try {
    return localStorage.getItem(FLAG) === '1';
  } catch {
    return false;
  }
}

function setWanted(on: boolean): void {
  try {
    if (on) localStorage.setItem(FLAG, '1');
    else localStorage.removeItem(FLAG);
  } catch {
    // storage unavailable: the browser subscription still stands for this session
  }
}

function keyBytes(b64u: string): Uint8Array {
  const pad = '='.repeat((4 - (b64u.length % 4)) % 4);
  const raw = atob((b64u + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

function sameKey(current: ArrayBuffer | null, key: string): boolean {
  if (!current) return false;
  const a = new Uint8Array(current);
  const b = keyBytes(key);
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/** The browser's subscription for the server's current key; an old key's subscription is replaced. */
async function subscribeBrowser(): Promise<PushSubscription> {
  const key = useSession.getState().meta?.push?.publicKey;
  if (!key) throw new Error('the server published no push key');
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    if (sameKey(existing.options.applicationServerKey, key)) return existing;
    await existing.unsubscribe();
  }
  return reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) as BufferSource });
}

async function registerWithServer(sub: PushSubscription): Promise<void> {
  const token = useSession.getState().token;
  if (!token) return;
  const json = sub.toJSON();
  await api.pushSubscribe(token, {
    endpoint: sub.endpoint,
    keys: { p256dh: json.keys?.['p256dh'] ?? '', auth: json.keys?.['auth'] ?? '' },
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    lang: navigator.language,
  });
}

async function unsubscribeBrowser(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    await sub?.unsubscribe();
  } catch {
    // no service worker or no subscription: nothing to undo
  }
}

function initialStatus(): PushStatus {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  return wanted() ? 'on' : 'off';
}

export const usePush = create<PushState>((set) => ({
  status: initialStatus(),
  busy: false,
  error: null,

  async enable() {
    if (!pushSupported()) return;
    set({ busy: true, error: null });
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        set({ status: permission === 'denied' ? 'denied' : 'off', busy: false });
        return;
      }
      await registerWithServer(await subscribeBrowser());
      setWanted(true);
      set({ status: 'on', busy: false });
    } catch (e) {
      set({ busy: false, error: e instanceof Error ? e.message : String(e) });
    }
  },

  async disable() {
    set({ busy: true, error: null });
    const token = useSession.getState().token;
    if (token) await api.pushUnsubscribe(token).catch(() => undefined);
    await unsubscribeBrowser();
    setWanted(false);
    set({ status: 'off', busy: false });
  },

  async test() {
    const token = useSession.getState().token;
    if (!token) return false;
    try {
      await api.pushTest(token);
      return true;
    } catch {
      return false;
    }
  },
}));

/**
 * Once signed in on the web: when the user wanted notifications here, make sure the browser
 * still holds a subscription for the server's current key and the server knows this zone.
 */
export async function startPushWatch(): Promise<void> {
  if (!pushSupported() || !wanted()) return;
  if (Notification.permission !== 'granted') {
    setWanted(false);
    usePush.setState({ status: Notification.permission === 'denied' ? 'denied' : 'off' });
    return;
  }
  try {
    await registerWithServer(await subscribeBrowser());
  } catch (e) {
    console.warn('push: could not renew the subscription', e);
  }
}

// Signing out takes the browser's subscription with it; the server row dies with the device.
sessionHooks.beforeSignOut.push(async () => {
  if (!pushSupported()) return;
  await unsubscribeBrowser();
  setWanted(false);
  usePush.setState({ status: Notification.permission === 'denied' ? 'denied' : 'off' });
});
